import { Link } from "react-router-dom";
import PublicHeader from "../components/PublicHeader";
import PublicFooter from "../components/PublicFooter";

const features = [
  { icon: "🗄️", title: "Tax Document Vault",     desc: "Securely store all SARS documents, IRP5s, IT3s, invoices and receipts — encrypted and organised by tax year." },
  { icon: "📅", title: "Tax Calendar",             desc: "All official SA SARS deadlines pre-loaded. Never miss an ITR12, EMP501 or VAT return again." },
  { icon: "📋", title: "SARS Submissions",         desc: "Track all your eFiling submissions, assessments, refunds and payments in one smart dashboard." },
  { icon: "🧮", title: "Tax Calculator",           desc: "Accurate SA income tax with full 2024/2025 SARS brackets, rebates, UIF and medical credits." },
  { icon: "💰", title: "Budget Planner",           desc: "Monthly budget tracking, income vs expense analysis and savings goal monitoring." },
  { icon: "💳", title: "Credit & Debt Tracker",   desc: "Manage home loans, vehicles and credit cards with interest rate tracking and payoff planning." },
  { icon: "📈", title: "Investment Portfolio",     desc: "Track shares, ETFs, unit trusts and RAs with real-time gain/loss across your full portfolio." },
  { icon: "📊", title: "Analytics & Insights",    desc: "Net worth dashboard, income trends, expense breakdown and full financial health score." },
  { icon: "🤖", title: "AI Tax Assistant",         desc: "Ask any SARS or South African tax question and get instant, accurate answers — 24/7." },
  { icon: "🌙", title: "Dark & Light Mode",        desc: "Premium interface with beautiful dark mode. Works perfectly on desktop, tablet and mobile." },
];

const plans = [
  {
    name: "Free",
    price: "R0",
    period: "/month",
    badge: null,
    features: ["5 Documents", "Tax Calendar", "Basic Calculator"],
    cta: "Start Free",
    highlight: false,
  },
  {
    name: "Individual Pro",
    price: "R129",
    period: "/month",
    badge: "Most Popular",
    features: ["20GB Storage", "All Features", "AI Tax Assistant", "Budget Planner", "Analytics"],
    cta: "Start Free Trial",
    highlight: true,
  },
  {
    name: "Business Pro",
    price: "R499",
    period: "/month",
    badge: null,
    features: ["200GB Storage", "10 Users", "VAT Tracking", "Priority Support"],
    cta: "Get Started",
    highlight: false,
  },
  {
    name: "Accountant Gold",
    price: "R799",
    period: "/month",
    badge: null,
    features: ["40 Clients", "Client Portal", "Team Access", "White-label Reports"],
    cta: "Get Started",
    highlight: false,
  },
];

const stats = [
  { value: "100%",   label: "SARS Compliant"  },
  { value: "256-bit", label: "SSL Encryption" },
  { value: "99.9%",  label: "Uptime"          },
  { value: "10+",    label: "Core Features"   },
];

const whys = [
  { icon: "🔒", title: "Bank-Level Security",   desc: "AES-256 encryption at rest. Every document, every transaction, fully protected." },
  { icon: "📱", title: "Any Device, Anywhere",  desc: "Full web app optimised for desktop and mobile — no app install required." },
  { icon: "🇿🇦", title: "Built for South Africa", desc: "Official SARS brackets, deadlines, rebates and tax forms — always up to date." },
  { icon: "⚡", title: "Instant Access",        desc: "Find any document, submission or financial record in seconds — not hours." },
];

export default function HomePage() {
  return (
    <div className="min-h-screen bg-luxury-bg">
      <PublicHeader />

      {/* ── HERO ──────────────────────────────────────────────────── */}
      <section className="pt-28 pb-24 bg-luxury-bg border-b border-white/[0.05] relative overflow-hidden">
        {/* Subtle gold glow background */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[700px] h-[400px] bg-luxury-gold/5 rounded-full blur-3xl" />
        </div>

        <div className="relative max-w-6xl mx-auto px-5 sm:px-8 text-center">
          {/* Pill badge */}
          <div className="inline-flex items-center gap-2 px-4 py-1.5 glass-gold rounded-full text-luxury-gold text-sm font-semibold mb-8">
            <span className="w-1.5 h-1.5 bg-luxury-gold rounded-full animate-pulse" />
            🇿🇦 Built exclusively for South African Taxpayers
          </div>

          <h1 className="text-5xl sm:text-6xl lg:text-7xl font-extrabold text-luxury-text leading-tight mb-6 tracking-tight">
            Your Tax Life,<br />
            <span className="text-luxury-gold">Perfectly Organised.</span>
          </h1>

          <p className="text-xl text-luxury-secondary max-w-2xl mx-auto mb-10 leading-relaxed">
            South Africa's most powerful tax document vault and personal finance platform. Store documents, track SARS submissions, calculate taxes — all in one secure place.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
            <Link to="/register"
              className="px-8 py-4 bg-luxury-gold hover:bg-luxury-gold-dim text-white font-bold rounded-xl text-lg transition-all shadow-lg hover:shadow-luxury-gold/30 hover:shadow-xl active:scale-95">
              Start Free Today →
            </Link>
            <a href="#features"
              className="px-8 py-4 border border-luxury-border text-luxury-secondary font-semibold rounded-xl text-lg hover:border-luxury-gold/40 hover:text-luxury-text transition-all">
              See All Features
            </a>
          </div>

          <p className="text-luxury-muted text-sm">No credit card required · Free plan available · Cancel anytime</p>

          {/* Stats row */}
          <div className="mt-16 grid grid-cols-2 sm:grid-cols-4 gap-4 max-w-2xl mx-auto">
            {stats.map(s => (
              <div key={s.label} className="glass rounded-2xl py-5 px-4 text-center">
                <div className="text-2xl font-extrabold text-luxury-gold">{s.value}</div>
                <div className="text-luxury-muted text-xs mt-1">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── FEATURES ──────────────────────────────────────────────── */}
      <section id="features" className="py-24 bg-luxury-bg">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <div className="text-center mb-14">
            <span className="text-luxury-gold text-xs font-semibold uppercase tracking-widest">Everything You Need</span>
            <h2 className="text-4xl font-extrabold text-luxury-text mt-2">10 Powerful Features</h2>
            <p className="text-luxury-secondary mt-3 max-w-lg mx-auto">Every feature designed specifically for South African taxpayers, businesses and accountants.</p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {features.map(f => (
              <div key={f.title}
                className="glass-card rounded-2xl p-6 group">
                <div className="text-3xl mb-4">{f.icon}</div>
                <h3 className="text-base font-bold text-luxury-text mb-2 group-hover:text-luxury-gold transition-colors">{f.title}</h3>
                <p className="text-luxury-secondary text-sm leading-relaxed">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── WHY TAXVAULT ──────────────────────────────────────────── */}
      <section id="security" className="py-24 bg-luxury-bg border-y border-white/[0.05] relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-14 items-center">
            <div>
              <span className="text-luxury-gold text-xs font-semibold uppercase tracking-widest">Why TaxVault?</span>
              <h2 className="text-4xl font-extrabold text-luxury-text mt-2 mb-8 leading-tight">
                Built for South Africa.<br />Built for You.
              </h2>
              <div className="space-y-6">
                {whys.map(item => (
                  <div key={item.title} className="flex gap-4">
                    <div className="flex-shrink-0 w-11 h-11 glass rounded-xl flex items-center justify-center text-xl">
                      {item.icon}
                    </div>
                    <div>
                      <h4 className="font-bold text-luxury-text">{item.title}</h4>
                      <p className="text-luxury-secondary text-sm mt-0.5 leading-relaxed">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Steps card */}
            <div className="glass-strong rounded-3xl p-8">
              <div className="flex items-center gap-2 mb-6">
                <span className="w-2 h-2 bg-luxury-gold rounded-full" />
                <h3 className="text-lg font-bold text-luxury-text">Start in 3 Simple Steps</h3>
              </div>
              {[
                { step: "01", title: "Create Your Free Account",  desc: "Sign up in under 2 minutes. No credit card needed." },
                { step: "02", title: "Upload Your Documents",     desc: "Drag and drop IRP5s, bank statements, invoices and more." },
                { step: "03", title: "Manage Everything",         desc: "Track SARS, calculate tax, plan budgets — one dashboard." },
              ].map((item, i) => (
                <div key={item.step} className={`flex gap-4 ${i < 2 ? "mb-6 pb-6 border-b border-luxury-border" : ""}`}>
                  <div className="flex-shrink-0 w-10 h-10 glass-gold rounded-xl flex items-center justify-center">
                    <span className="text-luxury-gold text-sm font-extrabold">{item.step}</span>
                  </div>
                  <div>
                    <h4 className="font-bold text-luxury-text text-sm">{item.title}</h4>
                    <p className="text-luxury-secondary text-xs mt-0.5 leading-relaxed">{item.desc}</p>
                  </div>
                </div>
              ))}
              <Link to="/register"
                className="mt-6 block w-full text-center py-3 bg-luxury-gold hover:bg-luxury-gold-dim text-white font-bold rounded-xl transition-all text-sm">
                Get Started Free →
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ── PRICING ───────────────────────────────────────────────── */}
      <section id="pricing" className="py-24 bg-luxury-surface/30 relative">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <div className="text-center mb-14">
            <span className="text-luxury-gold text-xs font-semibold uppercase tracking-widest">Simple Pricing</span>
            <h2 className="text-4xl font-extrabold text-luxury-text mt-2">Plans for Everyone</h2>
            <p className="text-luxury-secondary mt-3">From individuals to large accounting firms — there's a plan for you.</p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {plans.map(plan => (
              <div key={plan.name}
                className={`relative rounded-2xl p-6 flex flex-col border transition-all
                  ${plan.highlight
                    ? "bg-luxury-gold/10 border-luxury-gold/40 shadow-xl shadow-luxury-gold/10 scale-105"
                    : "bg-luxury-surface border-luxury-border hover:border-luxury-gold/20 hover:shadow-md"
                  }`}>
                {plan.badge && (
                  <span className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 bg-luxury-gold text-white text-xs font-bold rounded-full whitespace-nowrap">
                    {plan.badge}
                  </span>
                )}
                <h3 className="font-bold text-luxury-text text-base mb-1 mt-1">{plan.name}</h3>
                <div className="mb-5 mt-2">
                  <span className="text-4xl font-extrabold text-luxury-text">{plan.price}</span>
                  <span className="text-luxury-muted text-sm">{plan.period}</span>
                </div>
                <ul className="space-y-2 flex-1 mb-6">
                  {plan.features.map(f => (
                    <li key={f} className="text-sm flex items-center gap-2 text-luxury-secondary">
                      <span className="text-luxury-cta font-bold flex-shrink-0">✓</span> {f}
                    </li>
                  ))}
                </ul>
                <Link to="/register"
                  className={`text-center py-2.5 rounded-xl text-sm font-bold transition-all
                    ${plan.highlight
                      ? "bg-luxury-gold hover:bg-luxury-gold-dim text-white shadow-sm"
                      : "border border-luxury-border text-luxury-secondary hover:border-luxury-gold/40 hover:text-luxury-gold"
                    }`}>
                  {plan.cta}
                </Link>
              </div>
            ))}
          </div>
          <p className="text-center text-luxury-muted text-sm mt-8">
            All plans include a free trial. Full pricing on your{" "}
            <Link to="/register" className="text-luxury-gold hover:underline font-semibold">account dashboard</Link>.
          </p>
        </div>
      </section>

      {/* ── CTA BANNER ────────────────────────────────────────────── */}
      <section className="py-20 bg-luxury-bg border-y border-white/[0.05] text-center relative overflow-hidden">
        <div className="max-w-3xl mx-auto px-5">
          <div className="w-14 h-14 mx-auto mb-6 glass-gold rounded-2xl flex items-center justify-center">
            <span className="text-2xl">🚀</span>
          </div>
          <h2 className="text-4xl font-extrabold text-luxury-text mb-4">
            Ready to take control of your taxes?
          </h2>
          <p className="text-luxury-secondary text-lg mb-8 leading-relaxed">
            Join South African taxpayers who trust TaxVault Global to keep their financial lives organised and SARS-ready.
          </p>
          <Link to="/register"
            className="inline-block px-10 py-4 bg-luxury-gold hover:bg-luxury-gold-dim text-white font-bold rounded-xl text-lg transition-all shadow-lg hover:shadow-luxury-gold/30 hover:shadow-xl active:scale-95">
            Create Your Free Account Today →
          </Link>
          <p className="text-luxury-muted text-sm mt-4">No credit card required · Setup in 2 minutes</p>
        </div>
      </section>

      <PublicFooter />
    </div>
  );
}
